import {
  Box,
  Button,
  CircularProgress,
  Divider,
  IconButton,
  InputBase,
  Tooltip,
  Typography,
  useTheme,
} from '@mui/material';
import { tokens } from '../../global/theme/tokens';
import { useTranslation } from 'react-i18next';
import { ECTopbar } from './ECTopbar';
import FolderIcon from '@mui/icons-material/Folder';
import {
  forwardRef,
  Fragment,
  useEffect,
  useImperativeHandle,
  useRef,
  useState,
} from 'react';
import {
  API_GET,
  API_PATCH,
  API_POST,
  DBRequest,
  useConfig,
} from '../../api/api';
import { ExoSkeleton } from '../exo/ExoSkeleton';
import moment from 'moment';
import FlagIcon from '@mui/icons-material/Flag';
import OutlinedFlagIcon from '@mui/icons-material/OutlinedFlag';
import { updateOrCreate } from '../special/updateOrCreate';
import Loader from '../special/Loader';
import CreateIcon from '@mui/icons-material/Create';
import { ECSenderPreview } from './ECSenderPreview';
import CategoryIcon from '@mui/icons-material/Category';
import EmailIcon from '@mui/icons-material/Email';
import AttachmentIcon from '@mui/icons-material/Attachment';
import ExoInfiniteScroll from '../exo/ExoInfiniteScroll';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import NoDataAnimation from '../animation/NoDataAnimation';
import SearchIcon from '@mui/icons-material/Search';
import ClearIcon from '@mui/icons-material/Clear';
import AccountTreeIcon from '@mui/icons-material/AccountTree';
import SyncIcon from '@mui/icons-material/Sync';
import ReplyIcon from '@mui/icons-material/Reply';
import MailIcon from '@mui/icons-material/Mail';
import EventIcon from '@mui/icons-material/Event';
import ECFromStack from './ECFromStack';
import ECToStack from './ECToStack';
import { useDraggable } from '@dnd-kit/core';
import LockIcon from '@mui/icons-material/Lock';
import { Check } from '@mui/icons-material';
import ReplayIcon from '@mui/icons-material/Replay';

export const ECEmailsList = forwardRef(
  (
    {
      inbox,
      folderMap,
      onSelect,
      selectedEmail,
      onFolderChange,
      selectedFolder,
      onNewMail,
      noKeyAction,
      onEmailChange,
      credentials,
      onFolderSelect,
      onChangeView,
      isMobile,
      onDelete,
      hasDnd,
      outBoundStatusMap,
    },
    ref,
  ) => {
    const theme = useTheme();
    const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
    const config = useConfig();
    const { t } = useTranslation();

    const scrollContainerRef = useRef(null);
    const searchBoxRef = useRef(null);

    const { name, unseenCount, id, type, isOutgoing, api, defaultFilter } =
      inbox || {};

    const isOutBound = api === 'outbound-emails';

    const Icon = folderMap[name]?.Icon || FolderIcon;
    const label = folderMap[name]?.label || name;
    const color = folderMap[name]?.color || 'unset';

    const [apiData, setApiData] = useState(null);
    const syncButtonDelay = 2000;
    const [spinning, setSpinning] = useState(false);

    // Track deleted email IDs to prevent them from reappearing after periodic refresh
    const [deletedEmailIds, setDeletedEmailIds] = useState(new Set());

    // Filter state: null | 'unread' | 'flagged'
    const [activeFilter, setActiveFilter] = useState(null);

    // Search expansion state
    const [searchExpanded, setSearchExpanded] = useState(false);

    // Use ref to track previous folder state for change detection
    const prevFolderStateRef = useRef(null);

    // Reset deleted email IDs, filter and folder tracking when inbox changes
    useEffect(() => {
      setDeletedEmailIds(new Set());
      setActiveFilter(defaultFilter);
      prevFolderStateRef.current = null;
    }, [id]);

    useEffect(() => {
      loadEmails(id, false, type, defaultFilter, api);
    }, [id, config]);

    // Helper function to create a fingerprint of folder states
    const getFolderFingerprint = (credentialsData, folderName) => {
      if (!credentialsData) return null;
      const parts = [];
      credentialsData.forEach((cred) => {
        cred.inbox.folders?.forEach((folder) => {
          if (folder.name === folderName) {
            // Include both mailCount and unseenCount to detect any changes
            parts.push(
              `${cred.id}:${folder.id}:${folder.mailCount}:${folder.unseenCount}`,
            );
          }
        });
      });
      return parts.sort().join('|');
    };

    useEffect(() => {
      if (!credentials || !credentials.data || !selectedFolder) return;

      const isSmart = Boolean(type);

      if (isSmart) {
        // For smart folders: create a fingerprint of all matching folders
        const currentFingerprint = getFolderFingerprint(credentials.data, type);

        // Check if fingerprint changed (folder state changed)
        if (
          prevFolderStateRef.current !== null &&
          currentFingerprint !== prevFolderStateRef.current
        ) {
          loadEmails(id, true, type, activeFilter, api);
        }
        prevFolderStateRef.current = currentFingerprint;
      } else {
        // For normal folders: check specific folder by id
        var mailCountDiff = false;
        credentials?.data.forEach((cred) => {
          const folders = cred.inbox.folders;
          folders.forEach((folder) => {
            if (folder.id === selectedFolder.id) {
              mailCountDiff = folder.mailCount !== selectedFolder.mailCount;
              onFolderSelect(folder);
            }
          });
        });

        // reload list if there is something new in the selected folder
        if (mailCountDiff) loadEmails(id, true, type, activeFilter, api);
      }
    }, [credentials, type]);

    function loadEmails(
      inboxId,
      shadow,
      type,
      filter = null,
      apiEndpoint = null,
    ) {
      if (!shadow) setApiData(null);

      const isSmart = Boolean(type);
      let basePath = apiEndpoint
        ? `${apiEndpoint}?perPage=150`
        : isSmart
          ? `imap-emails?folderName[eq]=${type}&perPage=150`
          : `imap-emails?folderId[eq]=${inboxId}&perPage=150`;

      // Add filter parameter
      if (filter === 'unread') {
        basePath += '&isSeen[eq]=0';
      } else if (filter === 'flagged') {
        basePath += '&isFlagged[eq]=1';
      } else if (filter === 'queued') {
        basePath += '&status[ne]=1';
      } else if (filter === 'sent') {
        basePath += '&status[eq]=1';
      }

      DBRequest({
        config,
        path: basePath,
        onResponse: (response) => {
          setApiData(response);
        },
      });
    }

    function handleFilterChange(filter) {
      const newFilter = activeFilter === filter ? null : filter;
      setActiveFilter(newFilter);
      loadEmails(id, false, type, newFilter, api);
    }

    function handleDeleteEmail() {
      if (selection.length <= 0) return;
      // pretent the delete is successful
      const newEmails = apiData.data.filter(
        (obj) => !selection.includes(obj.id),
      );
      setApiData({ ...apiData, data: newEmails });
      // Track deleted email IDs to filter them out on refresh
      setDeletedEmailIds((prev) => new Set([...prev, ...selection]));
      onSelect(null);
      setSelection([]);
      calculateFolderUpdate(newEmails);

      DBRequest({
        config,
        path: `${api || 'imap-emails'}/bulk-delete`,
        method: API_POST({ emailIds: selection }),
      });
    }

    // search
    const [searchTerm, setSearchTerm] = useState('');
    const [searchResults, setSearchResults] = useState(null);
    const [searchLoading, setSearchLoading] = useState(false);

    useEffect(() => {
      if (!searchTerm) return;
      handleSearch(searchTerm);
    }, [selectedFolder]);

    function handleSearch(term) {
      const reqDataNormalFolder = { q: term, folderId: id };
      const reqDataSmartFolder = { q: term, type: type };

      if (term === '') {
        setSearchResults(null);
        setSearchLoading(false);
        return;
      }

      setSearchLoading(true);
      DBRequest({
        config,
        path: `imap-emails/search`,
        method: API_POST(type ? reqDataSmartFolder : reqDataNormalFolder),
        onResponse: handleSearchResponse,
      });
    }

    function handleSearchResponse(res) {
      setSearchResults(res);
      setSearchLoading(false);
    }

    // keyboard actions
    useEffect(() => {
      const handleKeyDown = (e) => {
        // Don't run mail shortcuts while a dialog/form is open. Otherwise the Delete key (e.g. while
        // filling the "new lead" form) deletes the selected email instead.
        const isModalOpen = document.querySelector('.MuiModal-root');
        if (isModalOpen) return;

        if (!noKeyAction && e.key === 'Delete') {
          handleDeleteEmail();
        }
      };

      window.addEventListener('keydown', handleKeyDown);
      return () => window.removeEventListener('keydown', handleKeyDown);
    }, [apiData, noKeyAction, selectedEmail, searchTerm]);

    function handleEmailChange(res) {
      onEmailChange(res);
      const newEmails = updateOrCreate(apiData, res.data);
      setApiData(newEmails);
      calculateFolderUpdate(newEmails.data);
    }

    function calculateFolderUpdate(emails) {
      // calc unseen email count
      var unseenCount = 0;
      emails.forEach((email) => {
        if (!email.isSeen) unseenCount++;
      });
      onFolderChange({ ...selectedFolder, unseenCount });
    }

    function updateEmail(email) {
      setApiData(updateOrCreate(apiData, email));
    }

    function deleteEmail(email) {
      if (!email) return;

      const newEmails = apiData.data.filter((obj) => email.id !== obj.id);
      setApiData({ ...apiData, data: newEmails });
      // Track deleted email ID to filter it out on refresh
      setDeletedEmailIds((prev) => new Set([...prev, email.id]));
      onSelect(null);
      setSelection([]);
      calculateFolderUpdate(newEmails);
    }

    // exposed functions
    useImperativeHandle(ref, () => ({
      updateEmail,
      deleteEmail,
    }));

    // selection stuff
    const [selection, setSelection] = useState([]);
    const [lastSelectedId, setLastSelectedId] = useState(null);

    const handleSelectionChange = (element, event) => {
      onSelect(element);
      const { id } = element;
      const isSelected = selection.includes(id);

      if (event.shiftKey && lastSelectedId !== null) {
        // SHIFT: Select range
        const startIndex = apiData.data.findIndex(
          (item) => item.id === lastSelectedId,
        );
        const endIndex = apiData.data.findIndex((item) => item.id === id);

        if (startIndex !== -1 && endIndex !== -1) {
          const [from, to] =
            startIndex < endIndex
              ? [startIndex, endIndex]
              : [endIndex, startIndex];
          const range = apiData.data.slice(from, to + 1).map((item) => item.id);
          const newSelection = Array.from(new Set([...selection, ...range])); // prevent duplicates
          setSelection(newSelection);
        }
      } else if (event.metaKey || event.ctrlKey) {
        // CTRL/CMD: Toggle selection
        if (isSelected) {
          setSelection(selection.filter((selId) => selId !== id));
        } else {
          setSelection([...selection, id]);
        }
        setLastSelectedId(id);
      } else {
        // Simple click: single selection
        setSelection([id]);
        setLastSelectedId(id);
      }
    };

    function handleSync() {
      if (spinning) return;
      setSpinning(true);
      DBRequest({ config, path: 'emails-sync', API_GET });
      setTimeout(
        () => loadEmails(id, true, type, activeFilter, api),
        syncButtonDelay,
      );
      setTimeout(() => setSpinning(false), syncButtonDelay);
    }

    return (
      <Box className="h-full flex flex-col max-w-100% w-full overflow-hidden relative max-w-[650px]">
        <ECTopbar
          label={label}
          sublabel={
            unseenCount > 0
              ? unseenCount == 1
                ? `${unseenCount} ${t('message')}`
                : `${unseenCount} ${t('messages')}`
              : ''
          }
        >
          {!isMobile &&
            credentials &&
            credentials?.data?.length > 0 &&
            !isOutBound && (
              <Box className="h-full flex items-center gap-1">
                <IconButton
                  onClick={() => handleSync()}
                  disabled={spinning}
                  size="small"
                >
                  <SyncIcon
                    className="flex justify-center items-center"
                    sx={{
                      animation: spinning
                        ? 'spin 0.5s linear infinite'
                        : 'none',
                      transformOrigin: '50% 49%',
                      backfaceVisibility: 'hidden',
                      willChange: 'transform',
                      '@keyframes spin': {
                        '0%': {
                          transform: 'rotate(360deg) translateZ(0)',
                        },
                        '100%': {
                          transform: 'rotate(0deg) translateZ(0)',
                        },
                      },
                    }}
                  />
                </IconButton>

                <Tooltip title={t('New Email')}>
                  <IconButton onClick={onNewMail} size="small">
                    <CreateIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            )}
        </ECTopbar>

        {/* Filter & Search Bar */}
        {!isMobile && credentials && credentials?.data?.length > 0 && (
          <Box className="flex gap-1 px-2 py-1">
            {/* Animated Search */}
            {!isOutBound && (
              <Box
                className="flex items-center overflow-hidden transition-all duration-300"
                sx={{
                  flex: searchExpanded ? 1 : 0,
                  width: searchExpanded ? 'auto' : '32px',
                  minWidth: '32px',
                  bgcolor: searchExpanded ? colors.bgInput : colors.card,
                  borderRadius: '8px',
                  border: searchExpanded
                    ? `1px solid ${colors.info}`
                    : '1px solid transparent',
                  '&:hover': {
                    bgcolor: searchExpanded ? colors.bgInput : colors.selected,
                  },
                }}
              >
                {searchExpanded ? (
                  <>
                    {searchLoading ? (
                      <CircularProgress
                        size={18}
                        sx={{ ml: 1, color: colors.info }}
                      />
                    ) : (
                      <SearchIcon
                        sx={{ fontSize: '18px', ml: 1, opacity: 0.5 }}
                      />
                    )}
                    <InputBase
                      placeholder={t('Search emails...')}
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') handleSearch(searchTerm);
                        if (e.key === 'Escape') {
                          setSearchExpanded(false);
                          setSearchTerm('');
                          setSearchResults(null);
                          setSearchLoading(false);
                        }
                      }}
                      size="small"
                      className="flex-1 px-2"
                      autoFocus
                      sx={{ fontSize: '13px' }}
                    />
                    <IconButton
                      size="small"
                      onClick={() => {
                        setSearchExpanded(false);
                        setSearchTerm('');
                        setSearchResults(null);
                        setSearchLoading(false);
                      }}
                    >
                      <ClearIcon sx={{ fontSize: '16px' }} />
                    </IconButton>
                  </>
                ) : (
                  <IconButton
                    size="small"
                    onClick={() => setSearchExpanded(true)}
                    sx={{ width: '100%', borderRadius: '8px' }}
                  >
                    <SearchIcon sx={{ fontSize: '18px' }} />
                  </IconButton>
                )}
              </Box>
            )}
            {/* Filter Buttons (hidden when search is expanded) */}
            {!searchExpanded && !isOutBound && (
              <>
                <Button
                  size="small"
                  onClick={() => handleFilterChange('unread')}
                  startIcon={<MailIcon sx={{ fontSize: '16px' }} />}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontSize: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    bgcolor:
                      activeFilter === 'unread' ? colors.info : colors.card,
                    color: activeFilter === 'unread' ? '#fff' : colors.text,
                    '&:hover': {
                      bgcolor:
                        activeFilter === 'unread'
                          ? colors.info
                          : colors.selected,
                    },
                  }}
                >
                  {t('Unread')}
                </Button>

                <Button
                  size="small"
                  onClick={() => handleFilterChange('flagged')}
                  startIcon={<FlagIcon sx={{ fontSize: '16px' }} />}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontSize: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    bgcolor:
                      activeFilter === 'flagged' ? colors.warning : colors.card,
                    color: activeFilter === 'flagged' ? '#fff' : colors.text,
                    '&:hover': {
                      bgcolor:
                        activeFilter === 'flagged'
                          ? colors.warning
                          : colors.selected,
                    },
                  }}
                >
                  {t('Flagged')}
                </Button>
              </>
            )}
            {isOutBound && (
              <>
                <Button
                  size="small"
                  onClick={() => handleFilterChange('queued')}
                  startIcon={<FlagIcon sx={{ fontSize: '16px' }} />}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontSize: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    bgcolor:
                      activeFilter === 'queued' ? colors.warning : colors.card,
                    color: activeFilter === 'queued' ? '#fff' : colors.text,
                    '&:hover': {
                      bgcolor:
                        activeFilter === 'queued'
                          ? colors.warning
                          : colors.selected,
                    },
                  }}
                >
                  {t('Queued')}
                </Button>
                <Button
                  size="small"
                  onClick={() => handleFilterChange('sent')}
                  startIcon={<Check sx={{ fontSize: '16px' }} />}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontSize: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    bgcolor:
                      activeFilter === 'sent' ? colors.success : colors.card,
                    color: activeFilter === 'sent' ? '#fff' : colors.text,
                    '&:hover': {
                      bgcolor:
                        activeFilter === 'sent'
                          ? colors.warning
                          : colors.selected,
                    },
                  }}
                >
                  {t('Sent')}
                </Button>
              </>
            )}
          </Box>
        )}
        {/* Mobile Filter & Search Bar */}
        {isMobile && credentials && credentials?.data?.length > 0 && (
          <Box className="flex gap-1 px-2 py-1">
            {/* Animated Search */}
            <Box
              className="flex items-center overflow-hidden transition-all duration-300"
              sx={{
                flex: searchExpanded ? 1 : 0,
                width: searchExpanded ? 'auto' : '40px',
                minWidth: '40px',
                bgcolor: searchExpanded ? colors.bgInput : colors.card,
                borderRadius: '8px',
                border: searchExpanded
                  ? `1px solid ${colors.info}`
                  : '1px solid transparent',
              }}
            >
              {searchExpanded ? (
                <>
                  {searchLoading ? (
                    <CircularProgress
                      size={18}
                      sx={{ ml: 1, color: colors.info }}
                    />
                  ) : (
                    <SearchIcon
                      sx={{ fontSize: '18px', ml: 1, opacity: 0.5 }}
                    />
                  )}
                  <InputBase
                    placeholder={t('Search emails...')}
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') handleSearch(searchTerm);
                      if (e.key === 'Escape') {
                        setSearchExpanded(false);
                        setSearchTerm('');
                        setSearchResults(null);
                        setSearchLoading(false);
                      }
                    }}
                    size="small"
                    className="flex-1 px-2"
                    autoFocus
                    sx={{ fontSize: '13px' }}
                  />
                  <IconButton
                    size="small"
                    onClick={() => {
                      setSearchExpanded(false);
                      setSearchTerm('');
                      setSearchResults(null);
                      setSearchLoading(false);
                    }}
                  >
                    <ClearIcon sx={{ fontSize: '16px' }} />
                  </IconButton>
                </>
              ) : (
                <IconButton
                  size="small"
                  onClick={() => setSearchExpanded(true)}
                  sx={{ width: '100%', borderRadius: '8px' }}
                >
                  <SearchIcon sx={{ fontSize: '18px' }} />
                </IconButton>
              )}
            </Box>

            {/* Filter Buttons (hidden when search is expanded) */}
            {!searchExpanded && (
              <>
                <Button
                  size="small"
                  onClick={() => handleFilterChange('unread')}
                  startIcon={<MailIcon sx={{ fontSize: '16px' }} />}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontSize: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    bgcolor:
                      activeFilter === 'unread' ? colors.info : colors.card,
                    color: activeFilter === 'unread' ? '#fff' : colors.text,
                    '&:hover': {
                      bgcolor:
                        activeFilter === 'unread'
                          ? colors.info
                          : colors.selected,
                    },
                  }}
                >
                  {t('Unread')}
                </Button>

                <Button
                  size="small"
                  onClick={() => handleFilterChange('flagged')}
                  startIcon={<FlagIcon sx={{ fontSize: '16px' }} />}
                  sx={{
                    flex: 1,
                    textTransform: 'none',
                    fontSize: '12px',
                    borderRadius: '8px',
                    border: 'none',
                    bgcolor:
                      activeFilter === 'flagged' ? colors.warning : colors.card,
                    color: activeFilter === 'flagged' ? '#fff' : colors.text,
                    '&:hover': {
                      bgcolor:
                        activeFilter === 'flagged'
                          ? colors.warning
                          : colors.selected,
                    },
                  }}
                >
                  {t('Flagged')}
                </Button>
              </>
            )}
          </Box>
        )}
        {isMobile && (
          <>
            <IconButton
              onClick={() => onChangeView('folder')}
              sx={{
                position: 'absolute',
                bgcolor: colors.bgInput,
                height: '50px',
                width: '50px',
                borderRadius: '15px',
              }}
              className="bottom-2 left-2 z-10 backdrop-blur-md"
            >
              <ArrowBackIosNewIcon sx={{ fontSize: '30px' }} />
            </IconButton>
            <IconButton
              onClick={onNewMail}
              sx={{
                position: 'absolute',
                bgcolor: colors.info,
                height: '50px',
                width: '50px',
              }}
              className="bottom-2 right-2 z-10"
            >
              <CreateIcon sx={{ fontSize: '30px' }} />
            </IconButton>
          </>
        )}
        <Box
          className="flex flex-col h-full overflow-y-auto relative w-full overflow-x-hidden"
          ref={scrollContainerRef}
        >
          {searchResults ? (
            searchResults?.data.length > 0 ? (
              <ExoInfiniteScroll
                apiData={searchResults}
                setApiData={setSearchResults}
                scrollContainerRef={searchBoxRef}
              >
                {searchResults?.data.map((email) => (
                  <Fragment key={email.id}>
                    <EmailCard
                      email={email}
                      onSelect={handleSelectionChange}
                      selected={selection.includes(email.id)}
                      onChange={handleEmailChange}
                      isOutgoing={isOutgoing}
                      hasDnd={hasDnd}
                      scrollContainerRef={scrollContainerRef}
                      outBoundStatusMap={outBoundStatusMap}
                    />
                    <Divider className="w-[90%] opacity-40" />
                  </Fragment>
                ))}
              </ExoInfiniteScroll>
            ) : (
              <Box className="w-full flex items-center justify-center pt-[12px]">
                <NoDataAnimation size="150px" />
              </Box>
            )
          ) : (
            <ExoInfiniteScroll
              apiData={apiData}
              setApiData={setApiData}
              scrollContainerRef={scrollContainerRef}
            >
              {apiData ? (
                apiData?.data.length > 0 ? (
                  apiData?.data
                    .filter((email) => !deletedEmailIds.has(email.id))
                    .map((email) => (
                      <Fragment key={email.id}>
                        <EmailCard
                          email={email}
                          onSelect={handleSelectionChange}
                          selected={selection.includes(email.id)}
                          onChange={handleEmailChange}
                          isOutgoing={isOutgoing}
                          hasDnd={hasDnd}
                          scrollContainerRef={scrollContainerRef}
                          outBoundStatusMap={outBoundStatusMap}
                        />
                        <Divider className="w-[90%] opacity-40" />
                      </Fragment>
                    ))
                ) : (
                  <Box className="w-full flex items-center justify-center pt-[12px]">
                    <NoDataAnimation size="150px" />
                  </Box>
                )
              ) : (
                <ExoSkeleton n={9} className="py-[2px] w-full" />
              )}
            </ExoInfiniteScroll>
          )}
        </Box>
      </Box>
    );
  },
);

export const EmailCard = ({
  email,
  onSelect,
  selected,
  onChange,
  isOutgoing,
  hasDnd,
  scrollContainerRef,
  outBoundStatusMap,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const { t } = useTranslation();
  const config = useConfig();
  const [isProcessing, setIsProcessing] = useState(false);
  const [isResending, setIsResending] = useState(false);

  const {
    id,
    subject,
    from,
    date,
    isSeen,
    isFlagged,
    salesObjectId,
    projectId,
    attachmentsCount,
    previousEmailsCount,
    isAnswered,
    to,
    icsData,
    smime,
    status,
    errorMessage,
  } = email;

  function getDateLabel(dateTime) {
    const dateMoment = moment(dateTime);
    const now = moment();

    if (dateMoment.isSame(now, 'day')) {
      return dateMoment.format('HH:mm');
    } else if (dateMoment.isSame(now.clone().subtract(1, 'day'), 'day')) {
      return `${t('yesterday')} ${dateMoment.format('HH:mm')}`;
    } else {
      return dateMoment.format(config.general.dateOutputFormat);
    }
  }

  function handleClickMail(e) {
    onSelect(email, e);
  }

  function handleFlagClick(e) {
    e.preventDefault();
    e.stopPropagation();
    DBRequest({
      config,
      path: `imap-emails/${id}`,
      method: API_PATCH({ isFlagged: !isFlagged }),
      onResponse: onChange,
      onLoading: setIsProcessing,
    });
  }

  const [dateTime, setDateTime] = useState('');
  useEffect(() => {
    if (config) setDateTime(getDateLabel(date));
  }, [config]);

  const Container = hasDnd ? DraggableContainer : Box;

  const StatusIcon = outBoundStatusMap?.[status]?.Icon;
  const statusColor = outBoundStatusMap?.[status]?.color;
  const statusLabel =
    status === 2 ? errorMessage : outBoundStatusMap?.[status]?.label;
  const isOutBound = outBoundStatusMap !== undefined && status !== undefined;

  function handleRetrySendEmail(email) {
    DBRequest({
      config,
      path: `outbound-emails/${email.id}/retry`,
      method: API_POST(),
      onResponse: onChange,
      onLoading: setIsResending,
    });
  }

  return (
    <Container
      draggableId={`email${email.id}`}
      email={email}
      scrollContainerRef={scrollContainerRef}
    >
      <Button
        sx={{
          justifyContent: 'start',
          borderRadius: '0px',
          bgcolor: selected
            ? colors.card
            : isFlagged
              ? colors.warning + '30'
              : 'transparent',
          height: '55px',
          border: `1px solid transparent`,
          '.only-on-hover': {
            transform: isFlagged ? 'translateX(0px)' : 'translateX(50px)',
            transition: 'transform 0.5s ease',
          },
          ':hover': {
            bgcolor: colors.info + '30',
            border: `1px solid ${colors.info}80`,
            '.only-on-hover': {
              transform: 'translateX(0px)',
            },
          },
        }}
        className="flex gap-1 px-2 w-full pl-1 pr-2"
        onClick={handleClickMail}
      >
        <Box
          sx={{
            bgcolor: isOutBound
              ? statusColor
              : isSeen
                ? 'transparent'
                : colors.info,
          }}
          className="w-[3px] h-full rounded-full flex items-center"
        >
          {isAnswered && (
            <ReplyIcon
              className=" -translate-x-[8px]"
              sx={{ fontSize: '11px', color: colors.grey[600] }}
            />
          )}
        </Box>
        {isOutBound && (
          <Tooltip title={statusLabel}>
            <StatusIcon sx={{ color: statusColor }} />
          </Tooltip>
        )}
        {isOutgoing ? (
          <Box className="w-full overflow-hidden">
            <ECToStack to={to} subject={subject} />
          </Box>
        ) : (
          <Box className="w-full overflow-hidden">
            <ECFromStack from={from} subject={subject} />
          </Box>
        )}
        <Box className="flex flex-col items-end overflow-hidden">
          <Box className="flex flex-row justify-center items-center">
            {smime && smime.isEncrypted && (
              <Tooltip title={t('Encrypted')}>
                <LockIcon
                  sx={{ fontSize: '14px', color: colors.text, opacity: 0.5 }}
                />
              </Tooltip>
            )}
            {!isOutBound && (
              <Box className={isProcessing ? '' : 'only-on-hover '}>
                <IconButton size="small" onClick={handleFlagClick}>
                  {isProcessing ? (
                    <CircularProgress size="14px" color="warning" />
                  ) : isFlagged ? (
                    <FlagIcon sx={{ fontSize: '14px', color: colors.warning }} />
                  ) : (
                    <OutlinedFlagIcon sx={{ fontSize: '14px' }} />
                  )}
                </IconButton>
              </Box>
            )}
          </Box>
          <Box className="w-full flex gap-0 overflow-hidden items-center">
            {salesObjectId && (
              <Tooltip title={t('linked to sales object')}>
                <Box className="w-[20px] h-[20px] flex items-center justify-center rounded-md">
                  <CategoryIcon sx={{ fontSize: '12px' }} color="info" />
                </Box>
              </Tooltip>
            )}
            {projectId && (
              <Tooltip title={t('linked to project')}>
                <Box className="w-[20px] h-[20px] flex items-center justify-center rounded-md">
                  <AccountTreeIcon sx={{ fontSize: '12px' }} color="info" />
                </Box>
              </Tooltip>
            )}
            {Array.isArray(icsData) && icsData.length > 0 && (
              <Tooltip title={t('Meeting Invitation')}>
                <Box className="w-[20px] h-[20px] flex items-center justify-center rounded-md">
                  <EventIcon sx={{ fontSize: '12px' }} color="success" />
                </Box>
              </Tooltip>
            )}
            {attachmentsCount > 0 && (
              <Tooltip
                title={`${attachmentsCount} ${
                  attachmentsCount === 1 ? t('attachment') : t('attachments')
                }`}
              >
                <Box className="w-[20px] h-[20px] flex items-center justify-center rounded-md opacity-50">
                  <AttachmentIcon
                    sx={{ fontSize: '12px' }}
                    className="ml-[2px]"
                  />
                </Box>
              </Tooltip>
            )}
            {previousEmailsCount > 0 && (
              <Tooltip
                title={`${previousEmailsCount} ${
                  previousEmailsCount === 1
                    ? t('message in conversation')
                    : t('messages in conversation')
                }`}
              >
                <Box className="w-[20px] h-[20px] flex items-center justify-center rounded-md opacity-50">
                  <EmailIcon sx={{ fontSize: '12px' }} className="ml-[2px]" />
                </Box>
              </Tooltip>
            )}
            <Typography
              textAlign="end"
              fontSize="9px"
              className=" whitespace-nowrap opacity-40 pr-3"
            >
              {dateTime}
            </Typography>
          </Box>
        </Box>
        {isOutBound && status !== 1 && (
          <Tooltip title={t('Retry sending email')}>
            {isResending ? (
              <Box sx={{ width: '25px', height: '25px' }}>
                <CircularProgress size="25px" color="info" />
              </Box>
            ) : (
              <IconButton onClick={() => handleRetrySendEmail(email)}>
                <ReplayIcon />
              </IconButton>
            )}
          </Tooltip>
        )}
      </Button>
    </Container>
  );
};

const DraggableContainer = ({
  draggableId,
  email,
  children,
  scrollContainerRef,
}) => {
  const theme = useTheme();
  const colors = tokens(theme.palette.mode, theme.palette.colorTheme);
  const [cursorOffset, setCursorOffset] = useState({ x: 0, y: 0 });
  // Capture cursor offset when drag starts
  const handlePointerDown = (event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const offsetX = event.clientX - rect.left; // cursor relative to element
    const scrollTop = scrollContainerRef.current.scrollTop;
    const offsetY = event.clientY - rect.top - scrollTop;
    setCursorOffset({ x: offsetX, y: offsetY });
  };

  // dnd stuff
  const { attributes, listeners, setNodeRef, transform, isDragging } =
    useDraggable({
      id: draggableId,
      activationConstraint: {
        delay: 150,
        tolerance: 5,
      },
      data: { email },
    });

  const dotSize = 10;

  const style = {
    position: isDragging ? 'fixed' : 'relative',
    pointerEvents: isDragging ? 'none' : 'auto',
    zIndex: isDragging ? 9999 : 'auto',
    overflow: isDragging ? 'hidden' : 'visible',
    transform: transform
      ? `translate(${transform.x + cursorOffset.x + dotSize}px, ${
          transform.y + cursorOffset.y + dotSize
        }px)`
      : undefined,

    maxWidth: isDragging ? dotSize : 400,
    maxHeight: isDragging ? dotSize : 80,
    borderRadius: isDragging ? '50%' : '0px',
    backgroundColor: isDragging ? '#1e90ff' : 'transparent',

    transition:
      'max-width 0.5s ease, max-height 0.6s ease, background-color 0.8s ease, border-radius 0.6s ease',
    cursor: 'grab',
  };
  return (
    <Box
      key={draggableId}
      ref={setNodeRef}
      onPointerDown={handlePointerDown}
      {...listeners}
      {...attributes}
      sx={style}
    >
      {children}
    </Box>
  );
};
